import { useTranslation } from 'react-i18next';
import { useSettings } from '@/hooks/useProducts';
import { formatCurrency } from '@/lib/utils';

interface VolumeRule {
  min_quantity: number;
  discount_type: string;
  discount_value: number;
  label?: string;
}

export default function VolumeDiscountTable({ rules, basePrice }: { rules?: VolumeRule[]; basePrice: number }) {
  const { t } = useTranslation('upsell');
  const { data } = useSettings();
  const config = data?.settings?.upsell_config?.volume_discounts;

  if (!config?.enabled || !config.show_table_on_product) return null;
  if (!rules || rules.length === 0) return null;

  const sorted = [...rules].sort((a, b) => a.min_quantity - b.min_quantity);

  function getPrice(rule: VolumeRule) {
    if (rule.discount_type === 'percentage') {
      return basePrice * (1 - rule.discount_value / 100);
    }
    if (rule.discount_type === 'fixed_amount') {
      return Math.max(0, basePrice - rule.discount_value);
    }
    if (rule.discount_type === 'fixed_price') {
      return rule.discount_value;
    }
    return basePrice;
  }

  function getSavings(rule: VolumeRule) {
    if (rule.discount_type === 'percentage') return t('volumeDiscount.percentOff', { value: rule.discount_value });
    if (rule.discount_type === 'fixed_amount') return t('volumeDiscount.saveAmount', { amount: formatCurrency(rule.discount_value) });
    return '';
  }

  return (
    <div className="rounded-lg border border-success-200 bg-success-50/50 overflow-hidden">
      <div className="px-4 py-2 bg-success-100/50 border-b border-success-200">
        <p className="text-sm font-semibold text-success-800">{t('volumeDiscount.heading')}</p>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-success-700 uppercase tracking-wider">
            <th className="px-4 py-2">{t('volumeDiscount.columns.quantity')}</th>
            <th className="px-4 py-2">{t('volumeDiscount.columns.priceEach')}</th>
            <th className="px-4 py-2">{t('volumeDiscount.columns.savings')}</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((rule, i) => (
            <tr key={i} className="border-t border-success-100">
              <td className="px-4 py-2 font-medium text-ink">
                {rule.label || t('volumeDiscount.minPlus', { count: rule.min_quantity })}
              </td>
              <td className="px-4 py-2 font-semibold text-success-700">
                {formatCurrency(getPrice(rule))}
              </td>
              <td className="px-4 py-2 text-success-600">
                {getSavings(rule)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
